import React from "react";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import useTranslation from "@/hooks/useTranslation";

const Terms = () => {
  const { t, language } = useTranslation();

  return (
    <div className="min-h-screen py-16">
      <SEO 
        title={`${t('termsOfService')} - NuWav Laboratories`}
        description={`${t('termsOfService')} for NuWav Laboratories. Legal information about using our website and medical testing services.`}
        keywords="terms of service, legal, website terms, laboratory services terms, medical testing terms, NuWav Laboratories policies"
        canonicalUrl={language === 'es' ? "https://nuwavlabs.com/es/terminos" : "https://nuwavlabs.com/terms"}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl font-bold mb-8 text-[#000080]">{t('termsOfService')}</h1>
          
          <div className="prose prose-lg">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-[#000080]">{t('agreementToTerms')}</h2>
              <p className="text-gray-700 mb-4">
                {t('agreementToTermsText')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-[#000080]">{t('servicesDescription')}</h2>
              <p className="text-gray-700 mb-4">
                {t('servicesDescriptionText')}
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>{t('coreLabTesting')}</li>
                <li>{t('molecularDiagnostic')}</li>
                <li>{t('clinicalChemistryAnalysis')}</li>
                <li>{t('diagnosticProcedures')}</li>
                <li>{t('resultsReporting')}</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-[#000080]">{t('professionalStandards')}</h2>
              <p className="text-gray-700 mb-4">
                {t('professionalStandardsText')}
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>{t('hipaaComplianceStandard')}</li>
                <li>{t('capAccreditationStandards')}</li>
                <li>{t('cliaCertificationRequirements')}</li>
                <li>{t('privacyShieldFramework')}</li>
                <li>{t('stateRegulations')}</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-[#000080]">{t('userResponsibilities')}</h2>
              <p className="text-gray-700 mb-4">
                {t('userResponsibilitiesText')}
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>{t('provideAccurateInfo')}</li>
                <li>{t('maintainConfidentiality')}</li>
                <li>{t('complyWithLaws')}</li>
                <li>{t('useLegitimateServices')}</li>
                <li>{t('respectIntellectualProperty')}</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-[#000080]">{t('sampleRequirements')}</h2>
              <p className="text-gray-700 mb-4">
                {t('sampleRequirementsText')}
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>{t('meetCollectionRequirements')}</li>
                <li>{t('properlyLabeled')}</li>
                <li>{t('properlyPackaged')}</li>
                <li>{t('includeRequisition')}</li>
                <li>{t('submitTimeframes')}</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-[#000080]">{t('resultsAndReports')}</h2>
              <p className="text-gray-700 mb-4">
                {t('resultsAndReportsText')}
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>{t('resultsProvidedTo')}</li>
                <li>{t('turnaroundTimes')}</li>
                <li>{t('criticalValues')}</li>
                <li>{t('resultsRequireCorrelation')}</li>
                <li>{t('reportsRetention')}</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-[#000080]">{t('limitationOfLiability')}</h2>
              <p className="text-gray-700 mb-4">
                {t('limitationOfLiabilityText')}
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700">
                <li>{t('indirectDamages')}</li>
                <li>{t('serviceDelays')}</li>
                <li>{t('improperSampleCollection')}</li>
                <li>{t('thirdPartyInterpretation')}</li>
                <li>{t('beyondControl')}</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-[#000080]">{t('changesToTerms')}</h2>
              <p className="text-gray-700 mb-4">
                {t('changesToTermsText')}
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 text-[#000080]">{t('contactInfo')}</h2>
              <p className="text-gray-700 mb-4">
                {t('contactInfoText')}
              </p>
              <address className="text-gray-700 not-italic">
                NuWav Laboratories<br />
                2015 Midway Rd Bldg 111<br />
                Carrollton, TX 75006
              </address>
            </section>

            <div className="text-gray-600 mt-8">
              {t('lastUpdated')}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Terms;
